import { useEffect, useState, useMemo } from 'react';
import { Users, Calendar, FileText, RefreshCw, ExternalLink } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function Commitments() {
  const [rolled, setRolled] = useState([]);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = async () => {
    setLoading(true);
    try {
      const [r1, r2] = await Promise.all([
        fetch(`${API_URL}/api/team-commitments`).then(r => r.json()),
        fetch(`${API_URL}/api/answer-log?is_enrollment_commit=true`).then(r => r.json()),
      ]);
      setRolled(Array.isArray(r1) ? r1 : []);
      setRecent(Array.isArray(r2) ? r2 : []);
    } catch {
      toast.error('Failed to load commitments');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!search) return rolled;
    const q = search.toLowerCase();
    return rolled.filter(r =>
      (r.sponsor || '').toLowerCase().includes(q) ||
      (r.pi_name || '').toLowerCase().includes(q) ||
      (r.indication || '').toLowerCase().includes(q)
    );
  }, [rolled, search]);

  const totals = useMemo(() => {
    let study_count = rolled.length;
    let total_committed = 0;
    rolled.forEach(r => {
      const max = Math.max(0, ...(r.patient_counts || []).map(p => p.value || 0));
      total_committed += max;
    });
    return { study_count, total_committed, log_entries: recent.length };
  }, [rolled, recent]);

  const fmtDate = (s) => {
    if (!s) return '-';
    try { return new Date(s).toLocaleString(); } catch { return s; }
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold text-slate-900 font-['Manrope']" data-testid="commitments-title">
            Team Commitments
          </h1>
          <p className="text-slate-600 mt-1">
            Every patient count we&apos;ve committed to a sponsor, auto-captured from inline answers.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={load} data-testid="refresh-btn">
            <RefreshCw className="w-4 h-4 mr-2" /> Refresh
          </Button>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="stryde-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-blue-100 text-blue-700 flex items-center justify-center">
              <FileText className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totals.study_count}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Active studies</p>
            </div>
          </div>
        </Card>
        <Card className="stryde-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-emerald-100 text-emerald-700 flex items-center justify-center">
              <Users className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totals.total_committed.toLocaleString()}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Patients committed</p>
            </div>
          </div>
        </Card>
        <Card className="stryde-card p-5">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-amber-100 text-amber-700 flex items-center justify-center">
              <Calendar className="w-5 h-5" />
            </div>
            <div>
              <p className="text-2xl font-bold">{totals.log_entries}</p>
              <p className="text-xs text-slate-500 uppercase tracking-wider">Answers logged</p>
            </div>
          </div>
        </Card>
      </div>

      {/* Rolled-up commitments */}
      <Card className="stryde-card">
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle className="text-xl font-['Manrope']">Per-study roll-up</CardTitle>
            <CardDescription>One row per sponsor + PI + indication, with submission dates and patient counts.</CardDescription>
          </div>
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search sponsor / PI / indication"
            className="max-w-sm"
            data-testid="commitments-search"
          />
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1,2,3].map(i => <Skeleton key={i} className="h-16" />)}</div>
          ) : filtered.length === 0 ? (
            <div className="text-center py-12 text-slate-500">
              No commitments logged yet. Use the extension&apos;s ⚡ Quick Answer on a feasibility form —
              every Insert click logs the answer and any patient counts here.
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 border-b border-slate-200">
                  <tr>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Sponsor</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">PI</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Indication</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Patients committed</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">First answer</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Last answer</th>
                    <th className="px-3 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider">Tracker</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100">
                  {filtered.map((r, idx) => {
                    const counts = (r.patient_counts || []).map(p => p.value).filter(Boolean);
                    const max = counts.length ? Math.max(...counts) : null;
                    const total = counts.reduce((a, b) => a + b, 0);
                    return (
                      <tr key={`${r.sponsor}-${r.pi_name}-${idx}`} className="hover:bg-slate-50">
                        <td className="px-3 py-3 font-medium text-slate-900">{r.sponsor || '-'}</td>
                        <td className="px-3 py-3 text-sm text-slate-600">{r.pi_name || '-'}</td>
                        <td className="px-3 py-3 text-sm text-slate-600">{r.indication || '-'}</td>
                        <td className="px-3 py-3">
                          {max ? (
                            <div className="flex flex-col gap-1">
                              <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border w-fit">
                                {max} pts (max)
                              </Badge>
                              {counts.length > 1 && (
                                <span className="text-xs text-slate-500">
                                  {counts.length} answers · total mentions: {total}
                                </span>
                              )}
                            </div>
                          ) : <span className="text-slate-400 text-sm">—</span>}
                        </td>
                        <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap">{fmtDate(r.first_at)}</td>
                        <td className="px-3 py-3 text-sm text-slate-600 whitespace-nowrap">{fmtDate(r.last_at)}</td>
                        <td className="px-3 py-3">
                          {r.tracker_entry_id ? (
                            <a href="/tracker" className="text-blue-600 hover:underline text-sm inline-flex items-center gap-1">
                              View <ExternalLink className="w-3 h-3" />
                            </a>
                          ) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent answer log */}
      <Card className="stryde-card">
        <CardHeader>
          <CardTitle className="text-xl font-['Manrope']">Recent enrollment answers</CardTitle>
          <CardDescription>Every inline answer that mentioned a patient count. Click to see the original question.</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="space-y-3">{[1,2].map(i => <Skeleton key={i} className="h-16" />)}</div>
          ) : recent.length === 0 ? (
            <div className="text-center py-8 text-slate-500 text-sm">No enrollment-style answers logged yet.</div>
          ) : (
            <div className="space-y-3">
              {recent.slice(0, 12).map((row) => (
                <div key={row.id} className="border border-slate-200 rounded-lg p-3 hover:border-slate-300">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-xs text-slate-500">{fmtDate(row.inserted_at)} · {row.submitted_by || 'unknown'}</span>
                    <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border">
                      {row.patient_count} pts{row.patient_count_min ? ` (${row.patient_count_min}-${row.patient_count_max})` : ''}
                    </Badge>
                  </div>
                  <p className="text-sm text-slate-700 font-medium line-clamp-2">{row.question}</p>
                  <p className="text-xs text-slate-500 mt-1">
                    {row.sponsor || 'No sponsor'} · {row.pi_name || 'No PI'} · {row.indication || 'No indication'}
                  </p>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
